import { useMemo, useState } from 'react';
import { Printer, RotateCcw, Gift, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';

const randomCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'WW-';
  for (let i = 0; i < 6; i += 1) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
};

const defaultExpiry = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

export default function GiftVoucherPage() {
  const [voucher, setVoucher] = useState({
    amount: '10',
    recipient: '',
    from: 'Whispering Wristwear',
    occasion: 'Raffle Prize',
    expiry: defaultExpiry(),
  });

  const initialCode = useMemo(() => randomCode(), []);
  const [code, setCode] = useState(initialCode);

  const handleChange = (field) => (e) => {
    setVoucher((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleNewCode = () => setCode(randomCode());

  const handlePrint = () => window.print();

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="gift-voucher-page">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * {
            visibility: visible;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-area {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
          .print-area img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12 no-print">
          <p className="text-caption text-[#84A98C] mb-4">Brand Assets</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Gift Voucher
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Customize and print a Whispering Wristwear gift voucher — perfect for raffles, gifts &amp; thank-yous.
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-start">
          {/* Voucher Preview */}
          <div className="flex justify-center">
            <div
              className="print-area"
              data-testid="voucher-preview"
            >
              <div
                className="relative bg-[#FAF9F6] shadow-float overflow-hidden flex"
                style={{ width: '680px', height: '360px' }}
              >
                {/* Outer gold borders wrapping the WHOLE voucher */}
                <div className="absolute inset-3 border-[1.5px] border-[#D4AF37] pointer-events-none z-10" />
                <div className="absolute inset-4 border-[0.5px] border-[#D4AF37]/40 pointer-events-none z-10" />

                {/* Photo side */}
                <div className="relative shrink-0" style={{ width: '260px' }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_morse-message-store/artifacts/sckiz8tg_AI%20image%207%20for%20Hand%20crafted%20whispering%20wristwear%20is%20a%20unique%20style%20of%20jewellery%20that%20has%20a%20hidden%20message.%20%20The%20bracelet%20is%20constructed%20using%20morse%20c.png"
                    alt="Whispering Wristwear bracelet"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
                  />
                  {/* warm overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#FAF9F6]/40" />
                  {/* brand badge */}
                  <div className="absolute top-7 left-7 flex items-center gap-2 bg-[#FAF9F6]/85 backdrop-blur-sm px-3 py-1.5 rounded-full z-20">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                    <span className="text-[9px] uppercase tracking-[0.25em] text-[#354F52]">
                      Whispering Wristwear
                    </span>
                  </div>
                </div>

                {/* Vertical divider between photo and content */}
                <div className="absolute left-[260px] top-6 bottom-6 w-px bg-[#D4AF37]/30 z-10" />

                {/* Content side */}
                <div className="relative flex-1 p-7">
                  {/* Top-right code */}
                  <div className="absolute top-7 right-9 text-right z-10">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#84A98C]">
                      Voucher Code
                    </p>
                    <p className="font-mono text-xs text-[#354F52] tracking-wider mt-0.5">
                      {code}
                    </p>
                  </div>

                  {/* Center content */}
                  <div className="absolute top-16 bottom-16 left-9 right-9 flex flex-col items-center justify-center text-center">
                    <p
                      className="text-[10px] uppercase tracking-[0.35em] text-[#84A98C] mb-2"
                      data-testid="voucher-occasion"
                    >
                      {voucher.occasion || 'Gift Voucher'}
                    </p>

                    <h2 className="font-serif italic text-2xl text-[#354F52] mb-1">
                      Gift Voucher
                    </h2>

                    <div className="my-1">
                      <span className="font-serif text-6xl text-[#D4AF37] leading-none">
                        €{voucher.amount || '0'}
                      </span>
                    </div>

                    <div className="my-2 opacity-80">
                      <MorseCodeVisualizer text="GIFT" size="sm" />
                    </div>

                    <p className="text-[10px] text-[#52796F] leading-snug mt-1 px-2">
                      Redeemable for any handcrafted Morse code bracelet at
                      <br />
                      <span className="text-[#354F52]">whisperingwristwear.com</span>
                    </p>
                  </div>

                  {/* Bottom-left recipient */}
                  <div className="absolute bottom-7 left-9">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#84A98C]">
                      Presented to
                    </p>
                    <p
                      className="font-serif text-sm text-[#354F52] italic mt-0.5"
                      data-testid="voucher-recipient"
                    >
                      {voucher.recipient || '________________'}
                    </p>
                  </div>

                  {/* Bottom-right expiry */}
                  <div className="absolute bottom-7 right-9 text-right">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#84A98C]">
                      Valid until
                    </p>
                    <p className="text-sm text-[#354F52] mt-0.5">
                      {voucher.expiry}
                    </p>
                  </div>
                </div>

                {/* Subtle decorative dots in corners (morse-code feel) */}
                <div className="absolute top-3 right-1/4 flex gap-1 z-20">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                  <span className="w-2 h-1 rounded-full bg-[#D4AF37]" />
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                </div>
                <div className="absolute bottom-3 right-1/4 flex gap-1 z-20">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                  <span className="w-2 h-1 rounded-full bg-[#D4AF37]" />
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-[#F5F5F0] rounded-sm p-6 space-y-5 no-print">
            <h3 className="font-serif text-xl text-[#354F52] flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              Customize
            </h3>

            <div className="space-y-2">
              <Label htmlFor="amount" className="text-[#354F52]">Amount (€)</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                value={voucher.amount}
                onChange={handleChange('amount')}
                data-testid="input-amount"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="occasion" className="text-[#354F52]">Occasion</Label>
              <Input
                id="occasion"
                value={voucher.occasion}
                onChange={handleChange('occasion')}
                placeholder="Raffle Prize"
                data-testid="input-occasion"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient" className="text-[#354F52]">Recipient name (optional)</Label>
              <Input
                id="recipient"
                value={voucher.recipient}
                onChange={handleChange('recipient')}
                placeholder="Leave blank for raffle winner"
                data-testid="input-recipient"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry" className="text-[#354F52]">Valid until</Label>
              <Input
                id="expiry"
                value={voucher.expiry}
                onChange={handleChange('expiry')}
                data-testid="input-expiry"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code" className="text-[#354F52]">Voucher code</Label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="font-mono"
                  data-testid="input-code"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleNewCode}
                  className="flex-shrink-0"
                  data-testid="regenerate-code-btn"
                >
                  <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
              <p className="text-xs text-[#84A98C]">
                Write this down somewhere when the winner redeems it.
              </p>
            </div>

            <Button
              onClick={handlePrint}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="print-voucher-btn"
            >
              <Printer className="w-4 h-4" strokeWidth={1.5} />
              Print Voucher
            </Button>

            <p className="text-xs text-[#84A98C] text-center">
              Tip: in the print dialog, choose "Save as PDF" for an emailable copy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
